import React, { useState } from 'react';

const GrammarUnit4Content = ({ unitData }) => {
  const [currentSection, setCurrentSection] = useState('basicAuxiliaryVerbs');
  const [showPractice, setShowPractice] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  // Safety check for unitData and content
  if (!unitData || !unitData.content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-100 to-red-100 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Unit 4 - Data Loading</h1>
            <p className="text-lg text-gray-600">Unit data is being loaded...</p>
          </div>
        </div>
      </div>
    );
  }

  const content = unitData.content;

  const handleAnswerSelect = (questionIndex, selectedOption) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionIndex]: selectedOption
    }));
  };

  const calculateScore = () => {
    if (!content.practiceQuestions) return { correct: 0, total: 0, percentage: 0 };
    
    let correct = 0;
    content.practiceQuestions.forEach((question, index) => {
      if (userAnswers[index] === question.correct) {
        correct++;
      }
    });
    return {
      correct,
      total: content.practiceQuestions.length,
      percentage: Math.round((correct / content.practiceQuestions.length) * 100)
    };
  };

  const renderBasicAuxiliaryVerbs = () => {
    const section = content.basicAuxiliaryVerbs;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        <h3 className="text-lg text-gray-600 mb-6">{section.subtitle}</h3>

        {/* Types */}
        {section.types && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-700 mb-3">助動詞分類</h4>
            {section.types.map((type, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg mb-3 border-l-4 border-red-500">
                <h5 className="font-semibold text-gray-800 mb-2">{type.name}</h5>
                <div className="mb-2">
                  {type.verbs.map((verb, vIndex) => (
                    <span key={vIndex} className="bg-red-500 text-white px-2 py-1 rounded text-sm mr-2">
                      {verb}
                    </span>
                  ))}
                </div>
                <p className="text-gray-600 text-sm">{type.description}</p>
              </div>
            ))}
          </div>
        )}

        {/* Usages */}
        {section.usages && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-700 mb-3">用法說明</h4>
            {section.usages.map((usage, index) => (
              <div key={index} className="bg-yellow-50 p-4 rounded-lg mb-3 border border-yellow-200">
                <h5 className="font-semibold text-yellow-800 mb-2">{usage.type}</h5>
                <p className="bg-white p-2 rounded font-mono text-gray-700 mb-2">{usage.pattern}</p>
                {usage.examples.map((example, eIndex) => (
                  <div key={eIndex} className="bg-gray-100 p-2 rounded text-sm text-gray-700 mb-1">
                    • {example}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Conjugations */}
        {section.conjugations && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-700 mb-3">{section.conjugations.title}</h4>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-lg shadow">
                <thead>
                  <tr className="bg-red-500 text-white">
                    <th className="p-3 text-left">原式</th>
                    <th className="p-3 text-left">過去式</th>
                    <th className="p-3 text-left">過去分詞</th>
                  </tr>
                </thead>
                <tbody>
                  {section.conjugations.verbs.map((verb, index) => (
                    <tr key={index} className={`border-b ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                      <td className="p-3 font-bold text-red-500">{verb.base}</td>
                      <td className="p-3">{verb.past}</td>
                      <td className="p-3">{verb.pastParticiple}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMayMight = () => {
    const section = content.mayMight;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        <h3 className="text-lg text-gray-600 mb-4">{section.subtitle}</h3>
        <p className="text-gray-700 mb-4">{section.explanation}</p>

        {/* Scale */}
        {section.scale && (
          <div className="bg-orange-100 p-4 rounded-lg mb-4">
            <h4 className="font-semibold text-orange-800 mb-2">{section.scale.title}</h4>
            <div className="text-orange-700">{section.scale.items.join(' → ')}</div>
          </div>
        )}

        {/* Usages */}
        {section.usages && (
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-700 mb-3">用法</h4>
            {section.usages.map((usage, index) => (
              <div key={index} className="bg-yellow-50 p-4 rounded-lg mb-3 border border-yellow-200">
                <h5 className="font-semibold text-yellow-800 mb-2">{usage.function}</h5>
                {usage.examples.map((example, eIndex) => (
                  <div key={eIndex} className="bg-gray-100 p-2 rounded text-sm text-gray-700 mb-1">
                    • {example}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Patterns */}
        {section.patterns && (
          <div>
            <h4 className="text-lg font-semibold text-gray-700 mb-3">特殊句型</h4>
            {section.patterns.map((pattern, index) => (
              <div key={index} className="bg-blue-50 p-4 rounded-lg mb-3 border border-blue-200">
                <h5 className="font-semibold text-blue-800 mb-2">{pattern.name}</h5>
                <p className="text-blue-700 italic mb-2">{pattern.meaning}</p>
                <div className="bg-gray-100 p-2 rounded text-sm text-gray-700">
                  • {pattern.example}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCanCould = () => {
    const section = content.canCould;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        <h3 className="text-lg text-gray-600 mb-4">{section.subtitle}</h3>

        {/* Comparison */}
        {section.comparison && (
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-700 mb-3">時態比較</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(section.comparison).map(([key, tenses]) => (
                <div key={key} className="bg-gray-50 p-3 rounded">
                  <h5 className="font-semibold text-gray-800 mb-2">{key}</h5>
                  <div className="text-sm text-gray-600">
                    {tenses.join(', ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Usages */}
        {section.usages && (
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-700 mb-3">用法</h4>
            {section.usages.map((usage, index) => (
              <div key={index} className="bg-yellow-50 p-4 rounded-lg mb-3 border border-yellow-200">
                <h5 className="font-semibold text-yellow-800 mb-2">{usage.function}</h5>
                {usage.examples.map((example, eIndex) => (
                  <div key={eIndex} className="bg-gray-100 p-2 rounded text-sm text-gray-700 mb-1">
                    • {example}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Patterns */}
        {section.patterns && (
          <div>
            <h4 className="text-lg font-semibold text-gray-700 mb-3">特殊句型</h4>
            {section.patterns.map((pattern, index) => (
              <div key={index} className="bg-blue-50 p-4 rounded-lg mb-3 border border-blue-200">
                <h5 className="font-semibold text-blue-800 mb-2">{pattern.name}</h5>
                <p className="text-blue-700 italic mb-2">{pattern.meaning}</p>
                <div className="bg-gray-100 p-2 rounded text-sm text-gray-700 mb-2">
                  • {pattern.example}
                </div>
                {pattern.alternatives && (
                  <div className="text-sm text-blue-600">
                    替代用法: {pattern.alternatives.join(', ')}
                  </div>
                )}
                {pattern.examples && pattern.examples.map((example, eIndex) => (
                  <div key={eIndex} className="bg-gray-100 p-2 rounded text-sm text-gray-700 mt-1">
                    • {example}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderWillWould = () => {
    const section = content.willWould;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        <h3 className="text-lg text-gray-600 mb-4">{section.subtitle}</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Will */}
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h5 className="font-semibold text-green-800 mb-3">Will</h5>
            {section.will.functions.map((func, index) => (
              <p key={index} className="text-green-700 text-sm mb-2">• {func}</p>
            ))}
            {section.will.examples.map((example, index) => (
              <div key={index} className="bg-gray-100 p-2 rounded text-sm mb-1">
                {example}
              </div>
            ))}
          </div>

          {/* Would */}
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h5 className="font-semibold text-red-800 mb-3">Would</h5>
            {section.would.functions.map((func, index) => (
              <p key={index} className="text-red-700 text-sm mb-2">• {func}</p>
            ))}
            {section.would.examples.map((example, index) => (
              <div key={index} className="bg-gray-100 p-2 rounded text-sm mb-1">
                {example}
              </div>
            ))}
          </div>
        </div>

        {/* Polite Requests */}
        {section.politeRequests && (
          <div>
            <h4 className="text-lg font-semibold text-gray-700 mb-3">{section.politeRequests.title}</h4>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              {section.politeRequests.patterns.map((pattern, index) => (
                <p key={index} className="text-blue-800 font-mono bg-white p-2 rounded mb-2">
                  {pattern}
                </p>
              ))}
              {section.politeRequests.examples.map((example, index) => (
                <div key={index} className="bg-gray-100 p-2 rounded text-sm mb-1">
                  • {example}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAdvancedModals = () => {
    const section = content.advancedModals;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>

        {section.sections && section.sections.map((subsection, index) => (
          <div key={index} className="mb-8 bg-purple-50 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-purple-700 mb-4">{subsection.subtitle}</h3>
            
            {subsection.sections && subsection.sections.map((subSubsection, subIndex) => (
              <div key={subIndex} className="mb-6">
                <h4 className="text-lg font-semibold text-purple-600 mb-3">{subSubsection.subtitle}</h4>
                
                {subSubsection.pattern && (
                  <div className="mb-3 p-3 bg-purple-100 rounded">
                    <strong className="text-purple-800">Pattern:</strong> 
                    <code className="ml-2 bg-white px-2 py-1 rounded text-sm">
                      {subSubsection.pattern}
                    </code>
                  </div>
                )}

                {subSubsection.patterns && (
                  <div className="mb-3">
                    <strong className="text-purple-800 block mb-2">Patterns:</strong>
                    {Object.entries(subSubsection.patterns).map(([pattern, meaning], i) => (
                      <div key={i} className="ml-4 mb-2">
                        <code className="bg-white px-2 py-1 rounded text-sm mr-2">{pattern}</code>
                        <span className="text-gray-700">→ {meaning}</span>
                      </div>
                    ))}
                  </div>
                )}

                {subSubsection.usage && (
                  <div className="mb-3">
                    <strong className="text-purple-800">Usage:</strong> 
                    <span className="ml-2">{subSubsection.usage}</span>
                    {subSubsection.chinese && (
                      <span className="ml-2 text-gray-600">({subSubsection.chinese})</span>
                    )}
                  </div>
                )}

                {subSubsection.examples && (
                  <div className="mb-3">
                    <strong className="text-purple-800 block mb-2">Examples:</strong>
                    <ul className="space-y-2">
                      {subSubsection.examples.map((example, i) => (
                        <li key={i} className="ml-4 p-2 bg-white rounded border-l-4 border-purple-300">
                          {example}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {subSubsection.scale && (
                  <div className="mb-3 p-3 bg-orange-100 rounded">
                    <strong className="text-orange-800">Strength Scale:</strong>
                    <div className="mt-2 text-orange-700">{subSubsection.scale}</div>
                  </div>
                )}

                {subSubsection.distinction && (
                  <div className="mb-3">
                    <strong className="text-purple-800 block mb-2">Important Distinction:</strong>
                    {Object.entries(subSubsection.distinction).map(([form, meaning], i) => (
                      <div key={i} className="ml-4 mb-2 p-2 bg-yellow-50 rounded">
                        <code className="bg-white px-2 py-1 rounded text-sm mr-2">{form}</code>
                        <span>→ {meaning}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderPracticeSection = () => {
    if (!content.practiceQuestions) return null;

    const score = showResults ? calculateScore() : null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b-3 border-blue-500 pb-3">
          練習題 - Practice Questions
        </h2>

        {!showResults ? (
          <>
            {content.practiceQuestions.map((question, qIndex) => (
              <div key={qIndex} className="bg-gray-50 p-5 rounded-lg mb-4 border border-gray-200">
                <h4 className="text-gray-800 mb-4 font-medium">
                  {qIndex + 1}. {question.question}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => handleAnswerSelect(qIndex, oIndex)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        userAnswers[qIndex] === oIndex 
                          ? 'border-blue-500 bg-blue-500 text-white' 
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            
            <button
              onClick={() => setShowResults(true)}
              disabled={Object.keys(userAnswers).length < content.practiceQuestions.length}
              className={`px-6 py-3 rounded-lg text-white font-medium transition-all ${
                Object.keys(userAnswers).length < content.practiceQuestions.length
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-500 hover:bg-green-600'
              }`}
            >
              提交答案 Submit Answers
            </button>
          </>
        ) : (
          <div>
            <div className={`p-5 rounded-lg mb-5 text-center ${
              score.percentage >= 70 ? 'bg-green-100' : 'bg-red-100'
            }`}>
              <h3 className={`text-xl font-bold mb-2 ${
                score.percentage >= 70 ? 'text-green-800' : 'text-red-800'
              }`}>
                測驗結果 - Test Results
              </h3>
              <p className={`text-2xl font-bold ${
                score.percentage >= 70 ? 'text-green-800' : 'text-red-800'
              }`}>
                {score.correct} / {score.total} ({score.percentage}%)
              </p>
            </div>

            {content.practiceQuestions.map((question, qIndex) => {
              const userAnswer = userAnswers[qIndex];
              const isCorrect = userAnswer === question.correct;
              
              return (
                <div key={qIndex} className={`p-4 rounded-lg mb-3 border-2 ${
                  isCorrect ? 'bg-green-100 border-green-300' : 'bg-red-100 border-red-300'
                }`}>
                  <h4 className={`font-medium mb-2 ${
                    isCorrect ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {qIndex + 1}. {question.question}
                  </h4>
                  <p className={`mb-2 ${
                    isCorrect ? 'text-green-800' : 'text-red-800'
                  }`}>
                    Your answer: {question.options[userAnswer]} {isCorrect ? '✓' : '✗'}
                  </p>
                  {!isCorrect && (
                    <p className="text-red-800 mb-2">
                      Correct answer: {question.options[question.correct]}
                    </p>
                  )}
                  <p className={`text-sm italic ${
                    isCorrect ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {question.explanation}
                  </p>
                </div>
              );
            })}

            <button
              onClick={() => {
                setShowResults(false);
                setUserAnswers({});
              }}
              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all"
            >
              重新測驗 Retake Test
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderMemoryTips = () => {
    const section = content.memoryTips;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4 border-b-3 border-yellow-500 pb-3">
          💡 {section.title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {section.tips.map((tip, index) => (
            <div key={index} className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-800 mb-2">{tip.category}</h4>
              <p className="text-yellow-700 text-sm">{tip.content}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (currentSection) {
      case 'basicAuxiliaryVerbs':
        return renderBasicAuxiliaryVerbs();
      case 'mayMight':
        return renderMayMight();
      case 'canCould':
        return renderCanCould();
      case 'willWould':
        return renderWillWould();
      case 'advancedModals':
        return renderAdvancedModals();
      case 'practice':
        return renderPracticeSection();
      case 'memoryTips':
        return renderMemoryTips();
      default:
        return renderBasicAuxiliaryVerbs();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 to-red-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            {unitData.title.english}
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            {unitData.subtitle.english}
          </p>
          
          {/* Navigation */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <button
              onClick={() => setCurrentSection('basicAuxiliaryVerbs')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'basicAuxiliaryVerbs' && !showPractice
                  ? 'border-orange-500 bg-orange-500 text-white'
                  : 'border-orange-500 text-orange-500 hover:bg-orange-50'
              }`}
            >
              Basic Auxiliary Verbs
            </button>
            <button
              onClick={() => setCurrentSection('mayMight')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'mayMight' && !showPractice
                  ? 'border-orange-500 bg-orange-500 text-white'
                  : 'border-orange-500 text-orange-500 hover:bg-orange-50'
              }`}
            >
              May/Might
            </button>
            <button
              onClick={() => setCurrentSection('canCould')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'canCould' && !showPractice
                  ? 'border-orange-500 bg-orange-500 text-white'
                  : 'border-orange-500 text-orange-500 hover:bg-orange-50'
              }`}
            >
              Can/Could
            </button>
            <button
              onClick={() => setCurrentSection('willWould')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'willWould' && !showPractice
                  ? 'border-orange-500 bg-orange-500 text-white'
                  : 'border-orange-500 text-orange-500 hover:bg-orange-50'
              }`}
            >
              Will/Would
            </button>
            <button
              onClick={() => setCurrentSection('advancedModals')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'advancedModals' && !showPractice
                  ? 'border-orange-500 bg-orange-500 text-white'
                  : 'border-orange-500 text-orange-500 hover:bg-orange-50'
              }`}
            >
              Advanced Modals
            </button>
            <button
              onClick={() => setCurrentSection('practice')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'practice'
                  ? 'border-red-500 bg-red-500 text-white'
                  : 'border-red-500 text-red-500 hover:bg-red-50'
              }`}
            >
              Practice
            </button>
            <button
              onClick={() => setCurrentSection('memoryTips')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'memoryTips'
                  ? 'border-yellow-500 bg-yellow-500 text-white'
                  : 'border-yellow-500 text-yellow-500 hover:bg-yellow-50'
              }`}
            >
              Memory Tips
            </button>
          </div>
        </div>

        {/* Content */}
        {renderContent()}
      </div>
    </div>
  );
};

export default GrammarUnit4Content;
